import { InputAdornment, Paper, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type Geometry from '@arcgis/core/geometry/Geometry';
import { getAppConfig, getAttributeServiceUrl } from '../../../utils/ReduxHelper';
import useDebounce from '../../../utils/hooks/DebounceHook.tsx';
import FieldNames from '../../../defaults/FieldNames.json';
import GeneralService from '../../../services/GeneralService';
const FeatFields = FieldNames.featureFields;
type BasicFeature = { attributes: any; geometry: Geometry };
type ExistingSubstitutionFeatureSearchProps = {
    setSelectedActionId: (id: number | null) => void;
    isPostFire: boolean;
    feature: BasicFeature;
    open: boolean;
    // this will just be an array of action attributes
    setActions: (actions: object[]) => void;
};


export default function ExistingSubstitutionFeatureSearch({
    setSelectedActionId,
    isPostFire,
    feature,
    open,
    setActions,
}: ExistingSubstitutionFeatureSearchProps) {
    const [searchText, setSearchText] = useState('');
    const [filteredActions, setFilteredActions] = useState<any[]>([]);
    const config: any = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    const debouncedQuery = useDebounce(searchText, 500);
    const [loadingActions, setLoadingActions] = useState(false);
    const [selectedActionRowModel, setSelectedActionRowModel] = useState<any>({
        ids: new Set(),
        type: 'include',
    });
    // Define action grid columns
    const actionColumns: GridColDef[] = useMemo(
        () => [
            {
                field: FeatFields.name,
                headerName: 'Name',
                flex: 1,
                minWidth: 200,
            },
            {
                field: FeatFields.agency,
                headerName: 'Ownership Agency',
                width: 180,
            },
            {
                field: FeatFields.initiationFiscalYear,
                headerName: 'Fiscal Year',
                width: 120,
            },
        ],
        []
    );
    const attributesUrl = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}`;
    const createWhereClause = (
        searchText: string,
        isPostFire: boolean,
        feature: { attributes: any; geometry: Geometry }
    ) => {
        searchText = searchText.trim().toLowerCase();
        const entityType = isPostFire ? 'PostFire' : 'Fuels';
        const clauses = [
            `${FeatFields.entityType} = '${entityType}'`,
            `${FeatFields.status} = '${feature.attributes?.[FeatFields.status]}'`,
            `${FeatFields.joinFeature} = '${feature.attributes?.[FeatFields.joinFeature]}'`,
            `${FeatFields.class} = '${feature.attributes?.[FeatFields.class]}'`,
            // Exclude the current feature from the search results
            `${FeatFields.joinID} <> ${feature.attributes?.[FeatFields.joinID]}`,
        ];
        // actions with same approval status and same geom type
        let addlWhere = `LOWER(${FeatFields.name}) LIKE '%${searchText}%'`;
        if (!isNaN(parseInt(searchText))) {
            addlWhere += ` OR  CAST(${FeatFields.joinID} AS CHAR(10)) LIKE '%${searchText}%'`;
        }
        clauses.push(`(${addlWhere})`);
        return `${clauses.join(' AND ')}`;
    };
    useEffect(() => {
        if (debouncedQuery && open && searchText && attributesUrl) {
            setLoadingActions(true);

            const where = createWhereClause(searchText, isPostFire, feature);
            GeneralService.queryCall(attributesUrl, {
                where: where,
                outFields: `${FeatFields.objectID},${FeatFields.name},${FeatFields.joinID},${FeatFields.joinFeature},${FeatFields.agency},${FeatFields.initiationFiscalYear},${FeatFields.calculatedArea},${FeatFields.class},${FeatFields.entityType}`,
                returnGeometry: false,
                orderByFields: `${FeatFields.name} ASC`,
            })
                .then((results) => {
                    const actionsWithRowId = results.map((action: any) => ({
                        ...action,
                        rowId: action[FeatFields.objectID],
                    }));
                    setActions(actionsWithRowId);
                    setFilteredActions(actionsWithRowId);
                })
                .catch((error) => {
                    console.error('Error loading actions:', error);
                    setActions([]);
                    setFilteredActions([]);
                })
                .finally(() => {
                    setLoadingActions(false);
                });
        } else if (!open && searchText) {
            setActions([]);
            setFilteredActions([]);
            setSearchText('');
        }
    }, [debouncedQuery, open, searchText, attributesUrl, isPostFire]);

    const handleActionSelectionChange = useCallback((newSelection: any) => {
        // Only allow single selection
        if (newSelection && newSelection.ids && newSelection.ids.size > 0) {
            const idsArray = Array.from(newSelection.ids);
            const latestSelection = idsArray[idsArray.length - 1];
            setSelectedActionId(Number(latestSelection));
            setSelectedActionRowModel({ ids: new Set([latestSelection]), type: 'include' });
        } else {
            setSelectedActionId(null);
            setSelectedActionRowModel({ ids: new Set(), type: 'include' });
        }
    }, []);

    return (
        <>
            <TextField
                fullWidth
                placeholder='Search for action by name or ID'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ mb: 2 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    },
                }}
            />

            {/* Actions Grid */}
            <Paper sx={{ mb: 3, height: 250 }}>
                <DataGridPremium
                    rows={filteredActions}
                    columns={actionColumns}
                    getRowId={(row) => row.rowId}
                    loading={loadingActions}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedActionRowModel}
                    onRowSelectionModelChange={handleActionSelectionChange}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'pointer',
                        },
                    }}
                />
            </Paper>
        </>
    );
}